import { auth, db } from '../firebaseConfig';
import { signInWithEmailAndPassword, signInWithCustomToken, createUserWithEmailAndPassword, updateProfile, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { defaultPreferences } from '../constants/defaultPreferences';
import { track } from '../utils/analytics';
import { termsAcceptanceRecord } from '../constants/legalVersion';

// Username lookups run server-side (functions/account): users/{uid} is readable
// only by its owner and admins, so the client can't query other users' docs.
const accountCall = async (name, data) => (await httpsCallable(getFunctions(getApp()), name)(data)).data;

function fireSignupWelcomeEmail() {
  track('signup');
  try {
    httpsCallable(getFunctions(getApp()), 'sendSignupWelcomeEmail')({}).catch((err) => {
      console.warn('[authService] sendSignupWelcomeEmail:', err?.message || err);
    });
  } catch (e) {
    console.warn('[authService] sendSignupWelcomeEmail callable:', e?.message || e);
  }
}



export const authService = {
  // Check if a username is available (case-insensitive).
  // Returns true = available, false = taken or invalid, null = couldn't check.
  // A signed-in user's own current username counts as available.
  checkUsernameAvailable: async (username) => {
    if (!username || username.trim().length < 3) return false;
    try {
      const { valid, available } = await accountCall('checkUsername', { username: username.trim() });
      return valid ? available : false;
    } catch (e) {
      // Offline or rate limited: show nothing rather than a false "available".
      console.warn('[authService] checkUsername:', e?.message || e);
      return null;
    }
  },

  // Login with email or username and password
  login: async (emailOrUsername, password) => {
    let email = emailOrUsername.trim();

    try {
      // No @: a username. The server checks the password and returns a
      // sign-in token, so the account's email never reaches the browser.
      let userCredential;
      if (!email.includes('@')) {
        let token = null;
        try {
          token = (await accountCall('usernameSignIn', { username: email, password })).token;
        } catch (e) {
          if (e?.code === 'functions/resource-exhausted') return { user: null, error: e.message || 'Too many attempts. Wait a minute and try again.' };
          if (e?.code === 'functions/permission-denied') return { user: null, error: 'This account has been disabled' };
          if (e?.code === 'functions/invalid-argument') return { user: null, error: 'Invalid email/username or password' };
          if (e?.code === 'functions/failed-precondition' || e?.code === 'functions/unavailable') return { user: null, error: e.message || 'Sign in with your email instead.' };
          throw e;
        }
        userCredential = await signInWithCustomToken(auth, token);
      } else {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      }
      track('login', { method: 'password' });

      // Get additional user data from Firestore
      let userData = null;
      
      // First check if user exists in Firestore
      const userRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists) {
        // User exists in Firestore, get their data
        userData = userDoc.data();
      } else  {
        throw new Error('User not found');
      }
      
      return { 
        user: {
          ...userCredential.user,
          ...userData
        }, 
        error: null 
      };
    } catch (error) {
      let errorMessage = 'An error occurred during login';
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email/username';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email/username or password';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Register new user
  // `consent` must be true: the unchecked-by-default 21+ / Terms checkbox.
  register: async (email, password, username, { consent = false } = {}) => {
    let userCredential = null;
    try {
      if (consent !== true) {
        return { user: null, error: 'Please confirm you are 21 or older and agree to the Terms and Privacy Policy.' };
      }
      // Validate username format (no Firestore needed)
      if (!username || username.trim().length < 3) {
        return { user: null, error: 'Username must be at least 3 characters' };
      }
      if (!/^[a-zA-Z0-9_]+$/.test(username.trim())) {
        return { user: null, error: 'Username can only contain letters, numbers and underscores' };
      }
      if (username.trim().length > 30) {
        return { user: null, error: 'Username must be 30 characters or fewer' };
      }
      const normalizedUsername = username.trim().toLowerCase();
      const usernameFree = async () => (await accountCall('checkUsername', { username: username.trim() })).available === true;

      // Check before creating the account, so a taken name never creates one.
      if (!(await usernameFree())) {
        return { user: null, error: 'Username is already taken' };
      }

      userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Create the Firestore user profile. The username itself is claimed
      // server-side right after (rules don't let clients write it).
      const userData = {
        uid: userCredential.user.uid,
        // The address as Auth stored it, so a later profile save never "changes" it.
        email: userCredential.user.email || email,
        phoneNumber: '',
        profilePhoto: '',
        createdAt: new Date(),
        preferences: defaultPreferences,
        termsAcceptance: termsAcceptanceRecord('email_signup', serverTimestamp()),
      };

      const userRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userRef, userData);
      let claimed = null;
      for (let attempt = 0; attempt < 2 && !claimed; attempt++) {
        try {
          claimed = await accountCall('setUsername', { username: username.trim() });
        } catch (e) {
          // The account and profile exist; only the name didn't save (network,
          // rate limit). Retry once, then keep the account and say so.
          console.warn('[authService] setUsername after register:', e?.message || e);
        }
      }
      if (claimed?.error) {
        // Taken since the pre-check: roll back (an Auth onDelete trigger
        // removes the profile doc this account just wrote).
        await userCredential.user.delete();
        return { user: null, error: claimed.error };
      }

      fireSignupWelcomeEmail();

      const updatedUser = await getDoc(userRef);
      
      return { 
        user: {
          ...userCredential.user,
          ...updatedUser.data()
        }, 
        error: null,
        ...(claimed?.username ? {} : { warning: "Your account is ready, but the username didn't save. Set it in your Profile." })
      };
    } catch (error) {
      // If auth user was created but Firestore write failed, clean up
      if (userCredential?.user) {
        try { await userCredential.user.delete(); } catch {}
      }
      console.error('Registration error:', error);
      let errorMessage = 'An error occurred during registration';
      if (String(error?.code || '').startsWith('functions/')) {
        errorMessage = error.code === 'functions/resource-exhausted'
          ? 'Too many attempts. Wait a minute and try again.'
          : "Couldn't reach the server to check the username. Please try again.";
        return { user: null, error: errorMessage };
      }
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account already exists with this email';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password is too weak';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        default:
          // Check for blocked requests
          if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
            errorMessage = 'Request blocked by browser extension. Please disable ad blockers or try incognito mode.';
          } else if (error.message && error.message.includes('Failed to fetch')) {
            errorMessage = 'Network request failed. Please check your internet connection and try again.';
          } else {
            errorMessage = error.message || 'Registration failed';
          }
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Update username (checks uniqueness, excludes current user's own username)
  updateUsername: async (uid, newUsername) => {
    if (!newUsername || newUsername.trim().length < 3) {
      return { error: 'Username must be at least 3 characters' };
    }
    if (!/^[a-zA-Z0-9_]+$/.test(newUsername.trim())) {
      return { error: 'Username can only contain letters, numbers and underscores' };
    }
    if (newUsername.trim().length > 30) {
      return { error: 'Username must be 30 characters or fewer' };
    }
    // Server checks uniqueness and writes the signed-in user's own doc (uid is
    // taken from the auth token, not this argument).
    try {
      const res = await accountCall('setUsername', { username: newUsername.trim() });
      return { error: res?.error || null };
    } catch (e) {
      return { error: e?.code === 'functions/resource-exhausted' ? 'Too many attempts. Wait a minute and try again.' : (e?.message || 'Could not save username') };
    }
  },

  // Logout
  logout: async () => {
    try {
      await auth.signOut();
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  },

  // Reset password using the public, rate-limited backend endpoint
  resetPassword: async (email) => {
    try {
      const response = await fetch('https://us-central1-bet-optimized-fe922.cloudfunctions.net/requestPasswordReset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset email');
      }

      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  },

  // Google Sign-In
  signInWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if user exists in Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      let userData;
      if (userDoc.exists()) {
        // User exists, update last login and any new Google data
        userData = userDoc.data();
        // Only the fields that change: re-sending the whole doc would also
        // re-send server-owned fields (plan, username) and fail the rules if
        // the server updated them since this read.
        await setDoc(userRef, {
          lastLogin: new Date(),
          displayName: user.displayName || userData.displayName,
          photoURL: user.photoURL || userData.photoURL,
          provider: 'google'
        }, { merge: true });
        // An account whose name never got claimed (e.g. the profile was
        // created by another code path first): claim one now.
        if (!userData.usernameLower) {
          try {
            // Keep a legacy handle (username without usernameLower) rather than renaming to the Google name.
            const claimed = await accountCall('setUsername', { username: userData.username || user.displayName || (user.email || '').split('@')[0], suffixIfTaken: true });
            if (claimed?.username) {
              userData = { ...userData, username: claimed.username, usernameLower: claimed.username.toLowerCase() };
            }
          } catch (e) {
            console.warn('[authService] Google username claim (existing):', e?.message || e);
          }
        }
      } else {
        // New user, create profile with Google data
        const rawUsername = user.displayName || user.email.split('@')[0];
        userData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          phoneNumber: user.phoneNumber || '',
          createdAt: new Date(),
          lastLogin: new Date(),
          provider: 'google',
          preferences: defaultPreferences
        };
        
        await setDoc(userRef, userData);
        // Claim a username derived from the Google name (numbered if taken).
        try {
          const claimed = await accountCall('setUsername', { username: rawUsername, suffixIfTaken: true });
          if (claimed?.username) {
            userData.username = claimed.username;
            userData.usernameLower = claimed.username.toLowerCase();
          }
        } catch (e) {
          console.warn('[authService] Google username claim:', e?.message || e);
        }
        fireSignupWelcomeEmail();
      }
      
      return { 
        user: {
          ...user,
          ...userData
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Google Sign-In error:', error);
      let errorMessage = 'An error occurred during Google Sign-In';
      
      // Handle specific Google Sign-In errors
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in was cancelled';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Popup was blocked by browser. Please allow popups and try again';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please try again later';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'An account already exists with this email using a different sign-in method';
          break;
        default:
          // Check for blocked requests
          if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
            errorMessage = 'Request blocked by browser extension. Please disable ad blockers or try incognito mode.';
          } else if (error.message && error.message.includes('Failed to fetch')) {
            errorMessage = 'Network request failed. Please check your internet connection and try again.';
          } else {
            errorMessage = error.message || 'Google Sign-In failed';
          }
      }
      
      return { user: null, error: errorMessage };
    }
  },
}; 